import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const WhatToExpectPage = () => {
  return (
    <>
      <SEO
        title="What to Expect from Therapy | Stillwater Therapy Dallas"
        description="An honest look at what therapy is like at Stillwater Therapy in Dallas, TX — the first session, early phase, timeline, and how it actually helps."
        path="/what-to-expect"
      />
      <Header />
      <main className="expect-page bg-background min-h-screen">
        <div className="expect-page-content max-w-3xl mx-auto px-6 py-16 md:py-24">
          {/* Back button + Header */}
          <motion.div {...fadeIn} className="expect-page-header mb-16">
            <Link to="/#what-to-expect">
              <Button className="expect-page-back mb-8 font-body font-medium tracking-wider text-xs uppercase">
                <ArrowLeft size={16} className="mr-2" />
                Back to Home
              </Button>
            </Link>
            <h1 className="expect-page-title text-3xl md:text-5xl font-heading font-medium text-foreground mb-6">
              What to Expect from Therapy
            </h1>
            <p className="expect-page-intro text-lg font-body text-muted-foreground font-light leading-relaxed">
              Starting therapy can feel like stepping into the unknown. You might wonder what it'll actually be like,
              whether it'll help, or how long it takes. Here's an honest look at the process — no jargon, no false
              promises.
            </p>
          </motion.div>

          {/* Section: What does a session look like? */}
          <motion.section {...fadeIn} className="expect-section mb-16">
            <h2 className="expect-section-heading text-2xl md:text-3xl font-heading font-medium text-foreground mb-4">
              What Does a Session Typically Look Like?
            </h2>
            <div className="space-y-4 font-body text-muted-foreground font-light leading-relaxed">
              <p>
                Sessions are typically 50 minutes, and most people come weekly to start. We can adjust the pace as you
                settle in.
              </p>
              <p>
                At the beginning of a session, I invite you to take a moment to check in with yourself. I may bring
                awareness to certain thoughts, feelings, or movements that I observe to engage both your unconscious
                experiences as well as your conscious experiences, approaching any stress and challenges from all
                sources in tandem.
              </p>
              <p>
                Your experience is reflected in many physical ways – posture, stiffness, tension, soreness, jitters, and
                even your nervous system. For the bulk of the session, we explore layer by layer how both your recent
                experiences and past traumas present themselves physically, mentally, and emotionally. Together, we
                bring awareness to each area and support you as a whole person. We bring gentle awareness to the tension
                areas and transition them to a state of ease.
              </p>
              <p>
                As we close the session, we may take a moment to transition you back to a place that prepares you for
                the rest of your day.
              </p>
            </div>
          </motion.section>

          {/* Section: The first session */}
          <motion.section {...fadeIn} className="expect-section mb-16">
            <h2 className="expect-section-heading text-2xl md:text-3xl font-heading font-medium text-foreground mb-4">
              The First Session
            </h2>
            <div className="space-y-4 font-body text-muted-foreground font-light leading-relaxed">
              <p>
                The first session is really about getting to know each other. I'll ask about what brought you here, a
                bit about your background, and what you're hoping to get out of this. You don't need to have it all
                figured out — most people don't.
              </p>
              <p>
                Think of it as a two-way interview. You're figuring out whether this feels like the right space for you,
                and I'm getting a sense of how I can best support you. There's no pressure to share more than you're
                comfortable with.
              </p>
              <p>
                By the end, we'll start to sketch out some loose goals — not a rigid plan, but a shared sense of
                direction. Something we can revisit and adjust as we go.
              </p>
            </div>
          </motion.section>

          {/* Section: The early phase */}
          <motion.section {...fadeIn} className="expect-section mb-16">
            <h2 className="expect-section-heading text-2xl md:text-3xl font-heading font-medium text-foreground mb-4">
              The First Few Weeks
            </h2>
            <div className="space-y-4 font-body text-muted-foreground font-light leading-relaxed">
              <p>
                The early sessions are about building a foundation. We're getting familiar with each other, and you're
                getting familiar with the process. Some people feel relief quickly; for others, it takes a bit longer to
                settle in. Both are completely normal.
              </p>
              <p>
                During this time, we'll refine the goals we set in the first session. How structured or open-ended we
                keep things is up to you — some people want a clear framework, and others prefer to follow what comes up
                naturally. We'll find what works for you.
              </p>
              <p>
                I'll check in regularly on how things feel. If something isn't working, I want to know. Therapy is most
                effective when you feel safe enough to be honest — including about the therapy itself.
              </p>
            </div>
          </motion.section>

          {/* Section: How will this help me? */}
          <motion.section {...fadeIn} className="expect-section mb-16">
            <h2 className="expect-section-heading text-2xl md:text-3xl font-heading font-medium text-foreground mb-4">
              How Will This Help Me?
            </h2>
            <div className="space-y-4 font-body text-muted-foreground font-light leading-relaxed">
              <p>
                Therapy helps you see things you can't see on your own. Not because something is wrong with you, but
                because it's genuinely hard to get perspective on your own life while you're living it.
              </p>
              <p>
                Over time, most people notice they react differently to things that used to overwhelm them. They
                understand their patterns better. They feel less stuck. The changes can be subtle at first — a pause
                before reacting, a shift in how you talk to yourself, a decision that finally feels clear.
              </p>
              <p>
                I won't tell you what to do or how to feel. What I will do is help you understand your own experience
                more clearly, so you can make choices that actually feel like yours.
              </p>
            </div>
          </motion.section>

          {/* Section: Timeline and expectations */}
          <motion.section {...fadeIn} className="expect-section mb-16">
            <h2 className="expect-section-heading text-2xl md:text-3xl font-heading font-medium text-foreground mb-4">
              How Long Does It Take?
            </h2>
            <div className="space-y-4 font-body text-muted-foreground font-light leading-relaxed">
              <p>
                This is the most honest answer I can give: I don't know. And I'd be cautious of anyone who claims they
                do.
              </p>
              <p>
                I can't promise you'll feel better in 12 sessions, or 24, or any specific number. What I can tell you is
                that most people start to notice shifts within the first month or two — not necessarily big
                breakthroughs, but small changes that add up.
              </p>
              <p>
                Healing isn't linear. Some weeks will feel like progress, and others will feel like you've gone
                backwards. That's not failure — it's how the process works. The timeline is yours. Some people come for
                a few months to work through something specific. Others stay longer because they find ongoing value in
                the work. There's no right answer.
              </p>
              <p>
                What matters is that you're the one deciding. I'll never keep you in therapy longer than you want to be,
                and I'll always be transparent about where I think we are.
              </p>
            </div>
          </motion.section>

          {/* Section: Your experience matters */}
          <motion.section {...fadeIn} className="expect-section mb-16">
            <h2 className="expect-section-heading text-2xl md:text-3xl font-heading font-medium text-foreground mb-4">
              Your Experience Is What Matters
            </h2>
            <div className="space-y-4 font-body text-muted-foreground font-light leading-relaxed">
              <p>
                You might come in with a diagnosis, or you might come in with just a feeling that something's off.
                Either way, I'm more interested in how you experience your life than in what category it fits into.
              </p>
              <p>
                Labels can be useful — they can validate what you're going through and point toward effective
                approaches. But they're not the whole picture. You're not a set of symptoms. You're a person navigating
                something difficult, and that's what we'll focus on.
              </p>
              <p>
                The goal is for you to leave therapy trusting yourself more — your instincts, your decisions, your
                ability to handle what life throws at you. Not because I gave you the answers, but because you found
                them.
              </p>
            </div>
          </motion.section>

          {/* CTA */}
          <motion.div {...fadeIn} className="expect-page-cta text-center pt-8 border-t border-border">
            <h3 className="text-xl md:text-2xl font-heading font-medium text-foreground mb-3">
              Ready to Take the First Step?
            </h3>
            <p className="font-body text-muted-foreground font-light mb-6">
              A free 15-minute consultation is a no-pressure way to see if we're a good fit.
            </p>
            <a href="https://ashley-bass.clientsecure.me/request/service" target="_blank" rel="noopener noreferrer">
              <Button className="expect-page-cta-button font-body font-medium tracking-wider text-xs uppercase">
                Get Started
              </Button>
            </a>
          </motion.div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default WhatToExpectPage;
